import Phaser from 'phaser';
import type { WireType } from '../sim/types';
import { COLORS, wireStyle } from './palette';
import { t } from '../i18n';
import { SANS, Panel, uiButton, clean, reducedMotion } from './theme';
import type { StringKey } from '../i18n/en';

/**
 * Lightweight popup explaining a new WIRE type. Localized via i18n —
 * keys are wire_<type>_title / _tagline / _b1..b3.
 */

/** Wire types that have intro popups (skip 'normal').  */
const WIRES_WITH_POPUPS: WireType[] = ['directional', 'delay', 'locked', 'forbidden', 'burn'];

function copyFor(type: WireType): { title: string; tagline: string; bullets: string[] } | null {
  if (!WIRES_WITH_POPUPS.includes(type)) return null;
  const k = (suffix: string) => `wire_${type}_${suffix}` as StringKey;
  return {
    title: t(k('title')),
    tagline: t(k('tagline')),
    bullets: [t(k('b1')), t(k('b2')), t(k('b3'))],
  };
}

export class WireIntroPopup {
  static show(scene: Phaser.Scene, type: WireType, ignoreFor?: Phaser.Cameras.Scene2D.Camera): Promise<void> {
    const copy = copyFor(type);
    if (!copy) return Promise.resolve();

    return new Promise((resolve) => {
      const { width, height } = scene.scale;
      const style = wireStyle(type);
      const accent = style.color;
      const accentHex = '#' + accent.toString(16).padStart(6, '0');

      const dim = scene.add
        .rectangle(0, 0, width, height, 0x000000, 0)
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(1750)
        .setInteractive();
      scene.tweens.add({ targets: dim, alpha: 0.82, duration: 220 });
      dim.on('pointerdown', () => close());

      const root = scene.add.container(0, 0).setDepth(1800).setScrollFactor(0);

      const cardW = Math.min(width - 90, 900);
      const cardH = 1180;
      const cx = width / 2;
      const cy = height / 2;
      const card = new Panel(scene, cx - cardW / 2, cy - cardH / 2, cardW, cardH, {
        fill: 0x0b1226, fillAlpha: 0.99, stroke: accent, strokeAlpha: 0.85,
      }).gfx;
      root.add(card);

      const bx = cx - cardW / 2;
      const by = cy - cardH / 2;

      const eyebrow = scene.add.text(cx, by + 39, clean(t('popup_new_wire')), {
        fontFamily: SANS,
        fontSize: '24px',
        color: '#8d98bd',
        fontStyle: '800',
      }).setOrigin(0.5);
      root.add(eyebrow);

      // === ANIMATED WIRE SAMPLE ===
      const sampleY = by + 195;
      const sampleW = cardW - 180;
      const sampleX0 = cx - sampleW / 2;
      const sampleX1 = cx + sampleW / 2;

      const nodeA = scene.add.circle(sampleX0, sampleY, 27, 0x10162a)
        .setStrokeStyle(3, 0x4a5a96, 1);
      const nodeB = scene.add.circle(sampleX1, sampleY, 27, 0x10162a)
        .setStrokeStyle(3, 0x4a5a96, 1);
      root.add([nodeA, nodeB]);

      const wireG = scene.add.graphics();
      const drawWire = () => {
        wireG.clear();
        wireG.lineStyle(15, accent, 0.14);
        wireG.lineBetween(sampleX0 + 27, sampleY, sampleX1 - 27, sampleY);
        wireG.lineStyle(8, accent, 0.22);
        wireG.lineBetween(sampleX0 + 27, sampleY, sampleX1 - 27, sampleY);
        wireG.lineStyle(style.width * 1.5, accent, 1);
        if (style.dashed) {
          const dash = 15;
          const gap = 12;
          let x = sampleX0 + 27;
          while (x < sampleX1 - 27) {
            const e = Math.min(x + dash, sampleX1 - 27);
            wireG.lineBetween(x, sampleY, e, sampleY);
            x += dash + gap;
          }
        } else {
          wireG.lineBetween(sampleX0 + 27, sampleY, sampleX1 - 27, sampleY);
        }
        if (style.arrow) {
          const x1 = sampleX1 - 27;
          wireG.fillStyle(accent, 1);
          wireG.fillTriangle(x1, sampleY, x1 - 21, sampleY - 12, x1 - 21, sampleY + 12);
        }
      };
      drawWire();
      wireG.setBlendMode(Phaser.BlendModes.NORMAL);
      root.add(wireG);

      const pulseFor = (type === 'forbidden') ? 0xff5577 : COLORS.pulse;
      const pulseHalo = scene.add.circle(sampleX0 + 27, sampleY, 27, pulseFor, 0.3)
        .setBlendMode(Phaser.BlendModes.ADD);
      const pulseDot = scene.add.circle(sampleX0 + 27, sampleY, 10, pulseFor, 1)
        .setBlendMode(Phaser.BlendModes.ADD);
      root.add([pulseHalo, pulseDot]);

      const pulseDuration = type === 'delay' ? 2200 : 1300;
      const pulseTween = scene.tweens.add({
        targets: [pulseHalo, pulseDot],
        x: sampleX1 - 27,
        duration: pulseDuration,
        repeat: -1,
        repeatDelay: 600,
        ease: type === 'delay' ? 'Sine.easeInOut' : 'Sine.easeIn',
        onRepeat: () => {
          pulseHalo.setPosition(sampleX0 + 27, sampleY);
          pulseDot.setPosition(sampleX0 + 27, sampleY);
        },
      });

      // === TEXT BLOCK ===
      const titleY = sampleY + 135;
      const title = scene.add.text(cx, titleY, copy.title, {
        fontFamily: SANS,
        fontSize: '64px',
        color: accentHex,
        fontStyle: '800',
      }).setOrigin(0.5);
      root.add(title);

      root.add(scene.add.rectangle(cx, titleY + 56, 105, 3, accent, 0.7));

      const tagline = scene.add.text(cx, titleY + 90, copy.tagline, {
        fontFamily: SANS,
        fontSize: '32px',
        color: '#cdd8f5',
        fontStyle: '800',
        wordWrap: { width: cardW - 120 },
        align: 'center',
      }).setOrigin(0.5, 0);
      root.add(tagline);

      let bulletY = titleY + 190;
      for (const b of copy.bullets) {
        const dot = scene.add.text(bx + 54, bulletY, '›', {
          fontFamily: SANS,
          fontSize: '32px',
          color: accentHex,
          fontStyle: '800',
        }).setOrigin(0, 0);
        const t = scene.add.text(bx + 90, bulletY, b, {
          fontFamily: SANS,
          fontSize: '30px',
          color: '#a2b0d8',
          wordWrap: { width: cardW - 150 },
          lineSpacing: 6,
        }).setOrigin(0, 0);
        root.add(dot);
        root.add(t);
        bulletY += Math.max(54, t.height + 18);
      }

      // === DISMISS BUTTON ===
      const btnY = by + cardH - 100;
      const btnC = uiButton(scene, cx, btnY, cardW - 120, 124, clean(t('got_it')), accent, () => close(), { primary: true, size: 36 });
      root.add(btnC);
      const btn = btnC.list[2] as Phaser.GameObjects.Rectangle;

      if (!reducedMotion()) {
        root.setAlpha(0);
        scene.tweens.add({ targets: root, alpha: 1, duration: 220 });
        card.setScale(0.94);
        scene.tweens.add({ targets: card, scale: 1, duration: 260, ease: 'Cubic.easeOut' });
      }

      if (ignoreFor) {
        ignoreFor.ignore(root);
        ignoreFor.ignore(dim);
      }

      let closing = false;
      const mountedAt = scene.time.now;
      const MIN_VISIBLE_MS = 120;
      const close = () => {
        if (closing) return;
        if (scene.time.now - mountedAt < MIN_VISIBLE_MS) return;
        closing = true;
        btn.disableInteractive();
        dim.disableInteractive();
        pulseTween.remove();
        scene.tweens.add({
          targets: [root, dim],
          alpha: 0,
          duration: 200,
          onComplete: () => { root.destroy(); dim.destroy(); resolve(); },
        });
      };
    });
  }
}
